import { Dayjs } from 'dayjs'
import { FC, RefObject, useEffect, useMemo, useRef, useState, VFC } from 'react'
import { Marker } from 'react-mark.js'
import { sendMessage } from 'src/api/contribution'
import { FeedbackScore } from 'src/components/Rating/FeedbackScore'
import { LargeButton } from 'src/elements/Button'
import styled, { css, CSSProperties } from 'styled-components'
import { useMasterData } from '../../api/masterData'
import {
  CONTRIBUTION_ANONYMOUS_MOSAIC,
  CONTRIBUTION_ARROW,
  CONTRIBUTION_BUDGET,
  CONTRIBUTION_BUY,
  CONTRIBUTION_CALENDAR,
  CONTRIBUTION_CONSULTATION,
  CONTRIBUTION_INTRODUCTION_TIMING,
  CONTRIBUTION_PR,
  CONTRIBUTION_USER,
  DEFAULT_IMAGE,
} from '../../assets/images'
import { MessagesIcon, PenIcon } from '../../assets/svgs'
import { CustomLinkify } from '../../components/Linkify'
import { Contribution } from '../../models/Contribution'
import {
  _contributionsBackground,
  _contributionsSpecialtyLabel,
  _menuicon,
  _profilegreen,
  contributionGray,
  defaultShadow,
  truewhite,
} from '../../styles/colors'
import { fontWeightBold } from '../../styles/font'
import { inversing } from '../../styles/mixins'
import { editContribution } from '../../utils/routes'
import { cardWidthMargin, ContributionButton, ContributionLink } from './styles'

const TITLE_MAX_LENGTH = 100
const BACKGROUND_HEIGHT = 300

type ContributionCardBaseProps = {
  contribution: Contribution
  keywords?: {
    buy: string[]
    sell: string[]
  }
}

type ContributionCardProps = ContributionCardBaseProps & {
  isMine?: boolean
  isFree?: boolean
  isPreview?: true
  isMyContributionsPage?: boolean
  showMessages?: (id: number) => void
  showProfile?: (userId: number) => void
  searchFreeWords?: string[]
}

export const ContributionCard: FC<ContributionCardProps> = ({
  contribution: {
    id,
    iconSrc,
    publishedAt,
    userId,
    name,
    companyName,
    // position,
    sell,
    buy,
    // detail,
    status,
    // paidPlan,
    // role,
    typeOfPurchase,
    consultation,
    // isBuy,
    // isConsultation,
    background,
    specialties: selectedIds,
    budget,
    introductionTiming,
    feedbackScore,
    feedbackCount,
  },
  isFree,
  ...props
}) => {
  const [showMore, setShowMore] = useState(false)
  const [backgroundHeight, setBackgroundHeight] = useState(0)
  const backgroundElement = useRef<HTMLInputElement>(null)
  const { data: masterData } = useMasterData()
  const specialties = masterData?.specialties
  const budgets = masterData?.budgets
  const introductionTimings = masterData?.introductionTimings
  const isMask = (): boolean => {
    // モザイクを外す（「導入したい」「相談したい」のモザイクを外す）
    return false
  }
  const onClickContact = async () => {
    // NOTE: 無料会員の場合はコンタクトボタンは非表示になる
    const res = await sendMessage(id)
    if (!res.error && res.data.roomUrl)
      if (!window.open(res.data.roomUrl, '__blank'))
        // NOTE: 非同期処理の場合はスマートフォンで window.open ができないケースがあるため
        window.location.href = res.data.roomUrl
      else window.open(res.data.roomUrl, '__blank')
  }

  const onClickShowProfile = () => {
    props.showProfile?.(userId)
  }

  const getTitle = (
    typeOfPurchase: Contribution['typeOfPurchase'],
    buy: string,
    consultation: string,
    sell: string,
  ) => {
    switch (typeOfPurchase) {
      case 'buy':
        return buy
      case 'consultation':
        return consultation
      case 'sell':
        return sell
    }
  }

  // NOTE: 背景の高さを取得 -> もっと見るボタンの表示/非表示をハンドリング
  useEffect(() => {
    if (backgroundElement)
      setBackgroundHeight(
        Number(
          JSON.stringify(
            backgroundElement.current?.getBoundingClientRect().height,
          ),
        ),
      )
  }, [backgroundElement])

  const keywords = useMemo(() => {
    switch (typeOfPurchase) {
      case 'buy':
        return props.keywords?.buy ?? []
      case 'sell':
        return props.keywords?.sell ?? []
      default:
        return []
    }
  }, [props.keywords, typeOfPurchase])

  return (
    <CardLayout publish={status == 'publish'}>
      <Header
        iconSrc={iconSrc}
        title={getTitle(typeOfPurchase, buy, consultation, sell)}
        typeOfPurchase={typeOfPurchase}
        publishedAt={publishedAt}
        name={name}
        companyName={companyName}
        keyword={keywords}
        searchFreeWords={props.searchFreeWords || []}
        isMask={isMask()}
        feedbackScore={feedbackScore || 0}
        feedbackCount={feedbackCount || 0}
      />
      <Background
        backgroundElement={backgroundElement}
        background={background}
        backgroundHeight={backgroundHeight}
        showMore={showMore}
        setShowMore={setShowMore}
        isPreview={props.isPreview}
        isMask={isMask()}
        keyword={keywords}
        searchFreeWords={props.searchFreeWords || []}
      />
      <SpecialtyLabels
        specialties={specialties
          ?.filter((item) => selectedIds?.includes(Number(item.value)))
          .map((item) => item.label)}
      />
      <Footer
        id={id}
        typeOfPurchase={typeOfPurchase}
        budget={budgets?.find((item) => item.value == budget)?.label}
        introductionTiming={
          introductionTimings?.find((item) => item.value == introductionTiming)
            ?.label
        }
        userId={userId}
        onClickShowProfile={onClickShowProfile}
        showMessages={props.showMessages}
        onClickContact={onClickContact}
        status={status}
        isFree={isFree}
        isMine={props.isMine}
        isPreview={props.isPreview}
        isMyContributionsPage={props.isMyContributionsPage}
      />
    </CardLayout>
  )
}

const Header: VFC<{
  iconSrc: string | undefined
  title: string
  typeOfPurchase: Contribution['typeOfPurchase']
  publishedAt: Dayjs
  name: string
  companyName: string
  keyword: string[]
  searchFreeWords: string[]
  isMask: boolean
  feedbackScore: number
  feedbackCount: number
}> = ({
  iconSrc,
  title,
  typeOfPurchase,
  publishedAt,
  name,
  companyName,
  keyword,
  searchFreeWords,
  isMask,
  feedbackScore,
  feedbackCount,
}) => {
  const reg = new RegExp(
    '(' + [...searchFreeWords, ...keyword].join('|') + ')',
    'g',
  )

  return (
    <HeaderDiv>
      <Icon src={isMask ? DEFAULT_IMAGE : iconSrc || DEFAULT_IMAGE} />
      <Captions>
        <Title lengthOver={title.length > TITLE_MAX_LENGTH}>
          <Marker mark={keyword}>
            <CustomLinkify reg={reg}>{title.replace(/\n/g, '')}</CustomLinkify>
          </Marker>
        </Title>
        <SubInformation
          typeOfPurchase={typeOfPurchase}
          publishedAt={publishedAt}
          name={name}
          companyName={companyName}
          isMask={isMask}
        />
        <FeedbackScore
          value={feedbackScore}
          feedbackCount={feedbackCount}
          yValue={'30px'}
        />
      </Captions>
    </HeaderDiv>
  )
}

type SubInformation = {
  typeOfPurchase: Contribution['typeOfPurchase']
  publishedAt?: Dayjs
  name: string
  companyName: string
  isAnonymous?: boolean
  isMask?: boolean
  style?: CSSProperties
  isTimeline?: boolean
}
export const getTypeOfPurchaseText = (
  typeOfPurchase: Contribution['typeOfPurchase'],
) => {
  switch (typeOfPurchase) {
    case 'buy':
      return '導入したい'
    case 'consultation':
      return '相談したい'
    case 'sell':
      return 'PRしたい'
  }
}

export const SubInformation: VFC<SubInformation> = ({
  typeOfPurchase,
  publishedAt,
  name,
  companyName,
  isAnonymous,
  isMask,
  style,
  isTimeline,
}) => {
  const getTypeOfPurchaseIcon = (
    typeOfPurchase: Contribution['typeOfPurchase'],
  ) => {
    switch (typeOfPurchase) {
      case 'buy':
        return CONTRIBUTION_BUY
      case 'consultation':
        return CONTRIBUTION_CONSULTATION
      case 'sell':
        return CONTRIBUTION_PR
    }
  }
  return (
    <SubInformationDiv style={style} isTimeline={isTimeline}>
      <div>
        <img src={getTypeOfPurchaseIcon(typeOfPurchase)} alt="" />
        <p>{getTypeOfPurchaseText(typeOfPurchase)}</p>
      </div>
      {publishedAt && (
        <div>
          <img src={CONTRIBUTION_CALENDAR} alt="" />
          <p>{publishedAt.format('YYYY/MM/DD HH:mm')}</p>
        </div>
      )}
      <div style={{ filter: isMask ? 'blur(5px)' : 'none' }}>
        <img src={CONTRIBUTION_USER} alt="" />

        {isAnonymous ? (
          <img src={CONTRIBUTION_ANONYMOUS_MOSAIC} alt="" />
        ) : (
          <p>{`${name}(${companyName})`}</p>
        )}
      </div>
    </SubInformationDiv>
  )
}

const Background: VFC<{
  backgroundElement: RefObject<HTMLDivElement>
  background: string | undefined
  backgroundHeight: number
  showMore: boolean
  setShowMore: (showMore: boolean) => void
  isPreview?: true
  isMask: boolean
  keyword: string[]
  searchFreeWords: string[]
}> = ({
  backgroundElement,
  background,
  backgroundHeight,
  showMore,
  setShowMore,
  isPreview,
  isMask,
  keyword,
  searchFreeWords,
}) => {
  const reg = new RegExp('(' + searchFreeWords.join('|') + ')', 'g')
  return (
    <>
      <BackgroundText
        ref={backgroundElement}
        showMore={showMore}
        height={backgroundHeight}
        isPreview={isPreview}
        style={{ filter: isMask ? 'blur(4px)' : 'none' }}
      >
        <Marker mark={keyword}>
          <CustomLinkify reg={reg}>{background || ''}</CustomLinkify>
        </Marker>
      </BackgroundText>
      {backgroundHeight > BACKGROUND_HEIGHT && !isPreview && (
        <SeeMoreButton showMore={showMore}>
          <img src={CONTRIBUTION_ARROW} alt="" />
          <button onClick={() => setShowMore(!showMore)}>
            {showMore ? '閉じる' : 'もっと見る'}
          </button>
        </SeeMoreButton>
      )}
    </>
  )
}

const SpecialtyLabels: VFC<{ specialties: string[] | undefined }> = ({
  specialties,
}) => {
  return (
    <SpecialtyLabelsDiv>
      {specialties?.map((name, index) => (
        <label htmlFor="" key={index}>
          {name}
        </label>
      ))}
    </SpecialtyLabelsDiv>
  )
}

const Footer: VFC<{
  id: number
  typeOfPurchase: Contribution['typeOfPurchase']
  budget: string | undefined
  introductionTiming: string | undefined
  userId: number
  onClickShowProfile: (userId: number) => void
  showMessages?: (id: number) => void
  onClickContact: () => void
  status: Contribution['status']
  isMine?: boolean
  isPreview?: boolean
  isMyContributionsPage?: boolean
  isFree?: boolean
}> = ({
  id,
  typeOfPurchase,
  budget,
  introductionTiming,
  userId,
  onClickShowProfile,
  showMessages,
  onClickContact,
  status,
  isMine,
  isPreview,
  isMyContributionsPage,
  isFree,
}) => {
  // コンタクトボタンの表示条件
  // 有料会員はすべて表示、無料会員は「PRしたい」のみ表示
  const shouldShowContactButton =
    !isMine && (!isFree || typeOfPurchase === 'sell')
  return (
    <FooterDiv>
      <FooterLeftDiv>
        {typeOfPurchase !== 'sell' && (
          <>
            {budget && (
              <div>
                <img src={CONTRIBUTION_BUDGET} alt="" />
                <p>
                  {budget} <span>/予算</span>
                </p>
              </div>
            )}
            {introductionTiming && (
              <div>
                <img src={CONTRIBUTION_INTRODUCTION_TIMING} alt="" />
                <p>
                  {introductionTiming} <span>/時期</span>
                </p>
              </div>
            )}
          </>
        )}
      </FooterLeftDiv>
      {!isPreview && (
        <Buttons>
          {isMyContributionsPage ? (
            // NOTE: 過去投稿一覧
            <>
              <ContributionLink href={editContribution(id)}>
                <PenIcon />
                <p>{status == 'publish' ? '編集・掲載停止' : '編集・再掲載'}</p>
              </ContributionLink>
              {showMessages && (
                <ContributionButton onClick={() => showMessages?.(id)}>
                  <MessagesIcon />
                  <p>メッセージを見る</p>
                </ContributionButton>
              )}
            </>
          ) : (
            // NOTE: 投稿一覧
            <>
              {isMine ? (
                // NOTE: 自分の投稿
                <>
                  <ContributionLink href={editContribution(id)}>
                    <PenIcon />
                    <p>
                      {status == 'publish' ? '編集・掲載停止' : '編集・再掲載'}
                    </p>
                  </ContributionLink>
                </>
              ) : (
                // NOTE: 他ユーザーの投稿
                <>
                  <ProfileButton onClick={() => onClickShowProfile(userId)}>
                    プロフィール詳細
                  </ProfileButton>
                  {shouldShowContactButton && (
                    <ContactButton onClick={onClickContact}>
                      コンタクト
                    </ContactButton>
                  )}
                </>
              )}
            </>
          )}
        </Buttons>
      )}
    </FooterDiv>
  )
}

const CardLayout = styled.div<{ publish: boolean }>`
  margin: 20px ${cardWidthMargin}px;
  padding: 24px;
  overflow: hidden;
  border-radius: 5px;
  height: fit-content;
  width: 936px;
  max-width: min(80vw, 936px);
  box-shadow: ${defaultShadow};
  background: ${({ publish }) =>
    publish ? truewhite : _contributionsBackground};
`

const HeaderDiv = styled.div`
  display: flex;
`

const Captions = styled.div`
  margin-left: 24px;
  font-size: 17px;
`

const Title = styled.h2<{ lengthOver: boolean }>`
  font-weight: ${fontWeightBold};
  word-break: break-all;

  a {
    color: ${_profilegreen};
  }
  ${({ lengthOver }) =>
    lengthOver &&
    `
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  `};
`

const SubInformationDiv = styled.div<{ isTimeline?: boolean }>`
  display: flex;
  margin-top: 10px;

  ${({ isTimeline }) =>
    css`
      font-size: ${isTimeline ? '14' : '12'}px;
    `}
  color: ${contributionGray};

  div {
    display: flex;
    margin-bottom: 5px;
  }

  img {
    ${({ isTimeline }) =>
      isTimeline
        ? css`
            height: 24px;
            width: 24px;
          `
        : css`
            height: 15px;
            width: auto;
          `}
  }

  p {
    ${({ isTimeline }) =>
      isTimeline &&
      css`
        line-height: 24px;
      `}
    margin-left: 5px;
    margin-right: 15px;
  }

  @media screen and (max-width: 540px) {
    display: block;
  }
`

const FooterDiv = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: 2px solid #f6f6f6;
  text-align: center;

  @media screen and (max-width: 720px) {
    flex-direction: column;
    align-items: center;
  }
`

const FooterLeftDiv = styled.div`
  display: flex;
  margin-top: 30px;
  width: 100%;

  div {
    display: flex;
    margin-bottom: 5px;
  }

  img {
    height: 20px;
    width: auto;
  }

  p {
    font-size: 15px;
    font-weight: ${fontWeightBold};
    margin-left: 5px;
    margin-right: 15px;
  }

  span {
    font-size: 12px;
    color: ${_menuicon};
  }

  @media screen and (max-width: 540px) {
    display: block;
  }
`

const Buttons = styled.div`
  display: flex;
  margin-top: 10px;
  font-weight: ${fontWeightBold};
  justify-content: center;
  align-items: center;

  > * {
    margin: 10px 0;
    margin-left: 10px;
  }

  @media screen and (max-width: 540px) {
    display: block;

    > * {
      margin-left: 0;
    }
  }
`

const BackgroundText = styled.div<{
  showMore: boolean
  height: number
  isPreview?: true
}>`
  width: 100%;
  font-size: 14px;
  margin-top: 24px;
  margin-bottom: 24px;
  line-height: 1.4;
  word-break: break-all;

  height: ${BACKGROUND_HEIGHT}px;
  overflow: hidden;
  ${({ showMore, height }) =>
    (showMore || height < BACKGROUND_HEIGHT) && `overflow: ''; height: auto;`};
  ${({ isPreview }) => isPreview && 'max-height: 500px; overflow: auto;'};

  a {
    color: ${_profilegreen};
  }
`

const SeeMoreButton = styled.div<{ showMore: boolean }>`
  width: 100%;
  margin-top: 10px;
  margin-bottom: 20px;
  text-align: center;

  button {
    font-size: 14px;
    margin-left: 10px;
    color: ${contributionGray};
  }

  img {
    height: 7px;
    width: auto;
    vertical-align: middle;
    ${({ showMore }) => !showMore && 'transform: rotate(180deg);'};
  }
`

const SpecialtyLabelsDiv = styled.div`
  margin-top: 10px;
  padding-bottom: 10px;

  label {
    display: inline-block;
    font-weight: ${fontWeightBold};
    margin-right: 5px;
    margin-bottom: 5px;
    padding: 12px 16px;
    background: ${_contributionsBackground};
    border-radius: 5px;
    border: 1px solid ${_contributionsSpecialtyLabel};
    cursor: auto;
  }
`

const Icon = styled.img`
  border-radius: 50%;
  object-fit: cover;
  width: 60px;
  height: 60px;
`

const ProfileButton = styled(LargeButton)`
  border: 1px solid ${_profilegreen};
  min-width: 204px;
  padding: 10px 0;

  ${inversing({
    color: _profilegreen,
    background: truewhite,
  })};
`

const ContactButton = styled(LargeButton)`
  min-width: 204px;
  padding: 10px 0;
  margin-left: 24px;
  border: solid ${_profilegreen} 1px;

  ${inversing({ color: truewhite, background: _profilegreen })};

  @media screen and (max-width: 540px) {
    margin-left: 0px;
  }
`
